/**
 * /api-keys — Master view of OAuth-issued tokens across products
 * (OCP-118). Each row is a portal_oauth_tokens record — the customer
 * sees which app holds which capabilities + can revoke or reissue.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api } from '../lib/api';
import { RoleReadOnlyNotice } from '../components/RoleReadOnlyNotice';
import { usePortalAccess } from '../lib/use-portal-access';
import { fmtDateTime } from '../lib/format-time';

interface ApiKey {
  id: string;
  client_id: string;
  product_family: 'core' | 'mail' | 'backup' | 'licenses' | 'connect' | 'other';
  scopes: string[];
  connection_id: string | null;
  hash_prefix: string;
  issued_at: string;
  expires_at: string;
  refresh_expires_at: string | null;
  revoked_at: string | null;
  /** OCP-779 - `revoking`: revoked here, OMS has not confirmed the Mail key is dead yet. */
  status: 'active' | 'revoking' | 'revoked' | 'expired';
}

type FamilyFilter = 'all' | 'core' | 'mail' | 'backup' | 'licenses' | 'connect' | 'other';
type StatusFilter = 'all' | 'active' | 'revoking' | 'revoked' | 'expired';

const FAMILIES: ApiKey['product_family'][] = ['core', 'mail', 'backup', 'licenses', 'connect', 'other'];

/** Badge colours per status; labels live in `api-keys:status.<key>` / `api-keys:family.<key>` (OCP-656). */
const STATUS_BADGE: Record<ApiKey['status'], string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  revoking: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
  revoked: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  expired: 'bg-amber-100 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200',
};

export function ApiKeysPage() {
  const { t } = useTranslation('api-keys');
  const { canManageInfra } = usePortalAccess();
  const [items, setItems] = useState<ApiKey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [familyFilter, setFamilyFilter] = useState<FamilyFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [actionPending, setActionPending] = useState<string | null>(null);
  const [detailModal, setDetailModal] = useState<ApiKey | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setError(null);
    try {
      const r = await api<{ items: ApiKey[] }>('/v1/portal/api-keys', { signal });
      setItems(r.items);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (err instanceof ApiError) setError(t('page.loadFailedHttp', { status: err.status }));
      else setError(t('page.loadFailed'));
    }
  }, [t]);

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const visible = useMemo(() => {
    if (!items) return [];
    return items.filter((k) => {
      if (familyFilter !== 'all' && k.product_family !== familyFilter) return false;
      if (statusFilter !== 'all' && k.status !== statusFilter) return false;
      return true;
    });
  }, [items, familyFilter, statusFilter]);

  const onRevoke = useCallback(
    async (id: string) => {
      if (!confirm(t('page.revokeConfirm'))) return;
      setActionPending(id);
      try {
        await api<ApiKey>(`/v1/portal/api-keys/${id}/revoke`, { method: 'POST' });
        void load();
      } catch (err) {
        if (err instanceof ApiError) setError(t('page.revokeFailedCode', { code: err.code ?? err.status }));
        else setError(t('page.revokeFailed'));
      } finally {
        setActionPending(null);
      }
    },
    [load, t],
  );

  const onReissue = useCallback(
    async (id: string) => {
      if (!confirm(t('page.reissueConfirm'))) return;
      setActionPending(id);
      try {
        const r = await api<{ reauthorize_url: string }>(
          `/v1/portal/api-keys/${id}/reissue`,
          { method: 'POST' },
        );
        window.location.assign(r.reauthorize_url);
      } catch (err) {
        setActionPending(null);
        if (err instanceof ApiError) setError(t('page.reissueFailedCode', { code: err.code ?? err.status }));
        else setError(t('page.reissueFailed'));
      }
    },
    [t],
  );

  return (
    <div className="mx-auto max-w-5xl">
      <RoleReadOnlyNotice scope="infra" />
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">{t('page.title')}</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{t('page.intro')}</p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600 dark:text-gray-400">
          {t('page.filterProduct')}
          <select
            value={familyFilter}
            onChange={(e) => setFamilyFilter(e.target.value as FamilyFilter)}
            className="ml-2 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
          >
            <option value="all">{t('page.all')}</option>
            {FAMILIES.map((f) => (
              <option key={f} value={f}>
                {t(`family.${f}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600 dark:text-gray-400">
          {t('page.filterStatus')}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="ml-2 rounded border border-gray-300 dark:border-gray-700 px-2 py-1 text-sm"
          >
            <option value="all">{t('page.all')}</option>
            <option value="active">{t('status.active')}</option>
            <option value="revoking">{t('status.revoking')}</option>
            <option value="revoked">{t('status.revoked')}</option>
            <option value="expired">{t('status.expired')}</option>
          </select>
        </label>
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!items && !error && <p className="text-sm text-gray-500">{t('page.loading')}</p>}

      {items && visible.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center text-sm text-gray-500">
          {items.length === 0 ? t('page.emptyNone') : t('page.emptyFiltered')}
        </div>
      )}

      {items && visible.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">{t('page.colProduct')}</th>
                <th className="px-3 py-2 text-left">{t('page.colConnection')}</th>
                <th className="px-3 py-2 text-left">{t('page.colScopes')}</th>
                <th className="px-3 py-2 text-left">{t('page.colStatus')}</th>
                <th className="px-3 py-2 text-right">{t('page.colActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {visible.map((k) => {
                const badge = { label: t(`status.${k.status}`), cls: STATUS_BADGE[k.status] };
                const isActive = k.status === 'active';
                return (
                  <tr key={k.id} data-testid={`api-key-row-${k.id}`}>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                      {t(`family.${k.product_family}`)}
                      <p className="mt-0.5 font-mono text-xs text-gray-400">
                        {k.hash_prefix}…
                      </p>
                    </td>
                    <td className="px-3 py-2">
                      {k.connection_id ? (
                        <span className="font-mono text-xs text-gray-700 dark:text-gray-300">
                          {k.connection_id}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
                      {k.scopes.join(', ') || <span className="text-gray-400">{t('page.none')}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        data-testid={`api-key-status-${k.id}`}
                        title={k.status === 'revoking' ? t('status.revokingHint') : undefined}
                        className={`rounded px-2 py-0.5 text-xs font-medium ${badge.cls}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => setDetailModal(k)}
                        className="mr-2 text-xs text-brand-700 underline"
                      >
                        {t('page.details')}
                      </button>
                      {isActive && canManageInfra && (
                        <>
                          <button
                            type="button"
                            onClick={() => onReissue(k.id)}
                            disabled={actionPending === k.id}
                            className="mr-2 text-xs text-brand-700 underline disabled:cursor-not-allowed disabled:text-gray-400"
                          >
                            {t('page.reissue')}
                          </button>
                          <button
                            type="button"
                            onClick={() => onRevoke(k.id)}
                            disabled={actionPending === k.id}
                            className="text-xs text-red-700 dark:text-red-300 underline disabled:cursor-not-allowed disabled:text-gray-400"
                          >
                            {t('page.revoke')}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailModal && (
        <DetailModal apiKey={detailModal} onClose={() => setDetailModal(null)} />
      )}
    </div>
  );
}

function DetailModal({ apiKey, onClose }: { apiKey: ApiKey; onClose: () => void }) {
  const { t } = useTranslation('api-keys');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="api-key-modal-title"
      data-testid="api-key-detail-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4"
    >
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-900 p-5 shadow-lg">
        <h2 id="api-key-modal-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {t('page.modalTitle')}
        </h2>
        <dl className="mt-4 space-y-2 text-sm">
          <div>
            <dt className="text-gray-500">{t('page.productFamily')}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{t(`family.${apiKey.product_family}`)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('page.clientId')}</dt>
            <dd className="font-mono text-xs text-gray-700 dark:text-gray-300">{apiKey.client_id}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('page.connectionId')}</dt>
            <dd className="font-mono text-xs text-gray-700 dark:text-gray-300">
              {apiKey.connection_id ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('page.scopes')}</dt>
            <dd className="text-gray-900 dark:text-gray-100">
              {apiKey.scopes.length > 0 ? (
                <ul className="mt-1 flex flex-wrap gap-1">
                  {apiKey.scopes.map((s) => (
                    <li
                      key={s}
                      className="rounded bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-300"
                    >
                      {s}
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="text-gray-400">{t('page.none')}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('page.issued')}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{formatDate(apiKey.issued_at)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">{t('page.expires')}</dt>
            <dd className="text-gray-900 dark:text-gray-100">{formatDate(apiKey.expires_at)}</dd>
          </div>
          {apiKey.revoked_at && (
            <div>
              <dt className="text-gray-500">{t('page.revoked')}</dt>
              <dd className="text-gray-900 dark:text-gray-100">{formatDate(apiKey.revoked_at)}</dd>
            </div>
          )}
        </dl>
        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {t('page.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return fmtDateTime(iso);
}
